/**
 * The per-VIDEO editorial PACK (media-model.md §3a) — the client-supplied fields that
 * become a rich `VideoObject` (seo.md §3, VS-2's videoObject() builder). Stored in the
 * media row's `metadata` JSONB bag (no migration; add fields freely). House-wide for any
 * video-production client — NOTHING hardcoded to a specific client.
 *
 * GRACEFUL BY DESIGN: every field is OPTIONAL. The effective floor is `title` +
 * `description` (with Stream's auto-derived thumbnail/duration/URLs, those two already
 * make a valid, ranking VideoObject). `description` is THE lever (flagged priority in the
 * UI); the rest are "the more you give, the richer it gets". The builder emits-what's-
 * present, so partial data → a leaner-but-valid schema, and nothing errors on a gap.
 *
 * Stream AUTO-derived fields (duration · dimensions · poster/thumbnail · contentUrl ·
 * embedUrl · uploadDate · encodingFormat) are NEVER asked here — the auto-vs-client split.
 *
 * transcript is captured only if the client already has one (a script/captions) — never
 * make them write one. It is stored INERT here; its 🔒 output rule (a .vtt <track> +
 * the JSON-LD `transcript` property, NO visible on-page block) lands at emission (VS-3),
 * not in this pack.
 */
export interface VideoKeyMoment {
  time: string; // a timecode, e.g. "0:15" or "1:02:30"
  label: string;
}

export type VideoPackFieldType = 'text' | 'textarea' | 'tags' | 'moments';

export interface VideoPackField {
  key: string; // the metadata JSONB key (camelCase, canonical)
  label: string;
  type: VideoPackFieldType;
  placeholder?: string;
  help?: string;
  priority?: boolean; // description = THE lever → surface it first / flag it
}

/** The 11 client-supplied PACK fields, in ask-order (title + description lead). */
export const VIDEO_PACK: VideoPackField[] = [
  { key: 'title', label: 'Title', type: 'text', placeholder: 'The video’s title' },
  {
    key: 'description',
    label: 'Description',
    type: 'textarea',
    placeholder: '2–4 keyword-rich sentences describing the video.',
    help: 'The priority field — the single most valuable thing to capture per video.',
    priority: true,
  },
  { key: 'client', label: 'Client / brand', type: 'text', placeholder: 'e.g. Patagonia' },
  { key: 'category', label: 'Category / type', type: 'text', placeholder: 'e.g. Brand film, Music video' },
  { key: 'credits', label: 'Credits', type: 'text', placeholder: 'e.g. Director — Jane Doe; DP — John Roe' },
  { key: 'year', label: 'Year', type: 'text', placeholder: 'e.g. 2025' },
  { key: 'tags', label: 'Tags / keywords', type: 'tags', placeholder: 'comma-separated, e.g. documentary, outdoors, 4k' },
  { key: 'location', label: 'Location', type: 'text', placeholder: 'e.g. Snowdonia, Wales' },
  {
    key: 'keyMoments',
    label: 'Key moments',
    type: 'moments',
    placeholder: '0:15 — Intro\n1:30 — The reveal',
    help: 'One per line: a timecode then the label (e.g. "0:15 — Intro").',
  },
  { key: 'posterTime', label: 'Preferred thumbnail time', type: 'text', placeholder: 'e.g. 0:08' },
  {
    key: 'transcript',
    label: 'Transcript / script',
    type: 'textarea',
    placeholder: 'Paste an existing script or captions (optional — never write one just for this).',
    help: 'Grab only if it already exists. Stored but never shown on the page.',
  },
];

// ── Pure serialize/parse for the two structured fields ────────────────────────
// Encodings are shared with the manifest importer (netlify/.../media/manifest.ts) — a
// tags/keyMoments cell parses the SAME way there, so a CSV/JSON manifest and the UI agree.

/** Split a multi-value string on newlines OR semicolons (labels may contain commas, so
 *  ';' — not ',' — separates key-moments; tags additionally split on commas). */
function splitLines(s: string): string[] {
  return s
    .split(/[\n;]+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/** "a, b; c" | ["a","b"] → string[] (trimmed, de-duped, empties dropped). */
export function parseTags(v: unknown): string[] {
  const raw =
    Array.isArray(v)
      ? v.map((x) => (typeof x === 'string' ? x : String(x ?? '')))
      : typeof v === 'string'
        ? v.split(/[\n;,]+/)
        : [];
  const out: string[] = [];
  for (const t of raw) {
    const clean = t.trim();
    if (clean && !out.includes(clean)) out.push(clean);
  }
  return out;
}

/** string[] → "a, b, c" for the UI input. */
export function serializeTags(tags: unknown): string {
  return Array.isArray(tags) ? tags.filter((t) => typeof t === 'string' && t.trim()).join(', ') : '';
}

const TIMECODE_RE = /^\s*(\d{1,2}(?::\d{2}){1,2})\s*[—–\-|]*\s*(.*)$/;

/** A key-moments string ("0:15 — Intro\n1:30 — Reveal" or ";"-separated) OR an array of
 *  {time,label} → VideoKeyMoment[]. A line with no leading timecode keeps time:'' so no
 *  data is silently lost; fully-empty parts are dropped. Graceful — never throws. */
export function parseKeyMoments(v: unknown): VideoKeyMoment[] {
  if (Array.isArray(v)) {
    return v
      .map((m) =>
        m && typeof m === 'object'
          ? { time: String((m as Record<string, unknown>).time ?? '').trim(), label: String((m as Record<string, unknown>).label ?? '').trim() }
          : { time: '', label: String(m ?? '').trim() },
      )
      .filter((m) => m.time || m.label);
  }
  if (typeof v !== 'string') return [];
  return splitLines(v)
    .map((part) => {
      const m = TIMECODE_RE.exec(part);
      return m ? { time: m[1], label: m[2].trim() } : { time: '', label: part };
    })
    .filter((m) => m.time || m.label);
}

/** VideoKeyMoment[] → "0:15 — Intro\n1:30 — Reveal" for the UI textarea. */
export function serializeKeyMoments(moments: unknown): string {
  if (!Array.isArray(moments)) return '';
  return moments
    .map((m) => {
      if (!m || typeof m !== 'object') return String(m ?? '').trim();
      const time = String((m as Record<string, unknown>).time ?? '').trim();
      const label = String((m as Record<string, unknown>).label ?? '').trim();
      return time && label ? `${time} — ${label}` : time || label;
    })
    .filter(Boolean)
    .join('\n');
}

/** Normalise a raw UI/manifest value for a pack field into its stored shape (arrays for
 *  tags/moments, trimmed string otherwise). Empty → null (caller drops the key). */
export function normalizePackValue(field: VideoPackFieldType, value: unknown): unknown {
  if (field === 'tags') {
    const arr = parseTags(value);
    return arr.length ? arr : null;
  }
  if (field === 'moments') {
    const arr = parseKeyMoments(value);
    return arr.length ? arr : null;
  }
  const s = typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
  return s || null;
}
